import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  OnDestroy,
  signal,
} from '@angular/core';
import { StorageService } from '@shared/services/storage.service';
import { StorageKey } from '@shared/enums/storage-keys.enum';

@Injectable({ providedIn: 'root' })
export class PasswordResetCooldownService implements OnDestroy {
  private readonly _storage = inject(StorageService);
  private readonly _destroyRef = inject(DestroyRef);

  private _intervalId: number | null = null;

  private readonly _cooldownUntilMs = signal<number>(
    Number(this._storage.read(StorageKey.passwordResetCooldownUntil)) || 0,
  );

  private readonly _nowMs = signal<number>(Date.now());

  public readonly remainingSeconds = computed(() => {
    // Depend on _nowMs so this recomputes every tick.
    const now = this._nowMs();
    const until = this._cooldownUntilMs();
    return Math.max(0, Math.ceil((until - now) / 1000));
  });

  public readonly isActive = computed(() => this.remainingSeconds() > 0);

  constructor() {
    this._cleanupExpired();
    if (this.isActive()) this._startTicker();
  }

  ngOnDestroy(): void {
    this._stopTicker();
  }

  public startCooldownMs(durationMs: number): void {
    const until = Date.now() + durationMs;
    this._cooldownUntilMs.set(until);
    this._storage.save(StorageKey.passwordResetCooldownUntil, until);
    this._startTicker();
  }

  public clearCooldown(): void {
    this._cooldownUntilMs.set(0);
    this._storage.remove(StorageKey.passwordResetCooldownUntil);
    this._stopTicker();
  }

  private _cleanupExpired(): void {
    const until = this._cooldownUntilMs();
    if (!until) return;
    if (until <= Date.now()) this.clearCooldown();
  }

  private _startTicker(): void {
    if (this._intervalId != null) return;
    this._intervalId = window.setInterval(() => {
      this._nowMs.set(Date.now());
      if (!this.isActive()) this.clearCooldown();
    }, 1000);
  }

  private _stopTicker(): void {
    if (this._intervalId == null) return;
    window.clearInterval(this._intervalId);
    this._intervalId = null;
  }
}
